<template>
  <div>
    <v-row>
      <v-col cols="12" md="8">
        <!-- Roll Up item edit/create -->
        <RollUpItem ref="rollUpItem" />
        <!-- Roll up items table -->
        <v-card outlined>
          <v-data-table
            hide-default-footer
            :headers="headers"
            :items="localFees"
          >
            <template #[`item.feeName`]="{ item }">
              <span
                >{{ item.feeName }}
                {{ item.attractGct ? '(Tax incl.)' : '' }}</span
              >
            </template>
            <template #[`item.rate`]="{ item }">
              <span
                >{{
                  item.attractGct
                    ? (item.feeRatePercentageWithGct || 0) + ' %'
                    : (item.feeRate || 0) + ' %'
                }}
                ({{
                  (item.attractGct
                    ? item.variableAmountWithGct
                    : item.variableAmount) | currency
                }})</span
              >
            </template>

            <template #[`item.percentage`]="{ item }">
              <span>{{ (item.rollUpPercentage || 0) + ' %' }}</span>
            </template>
            <template #[`item.flatFee`]="{ item }">
              <span>{{
                (item.attractGct ? item.flatFeeWithGct || 0 : item.flatFee || 0)
                  | currency
              }}</span>
            </template>
            <template #[`item.amount`]="{ item }">
              <span>{{ (item.rollUpAmount || 0) | currency }}</span>
            </template>
            <template #no-data>
              <NoData />
              <h2 class="mb-2 subtitle-1">No items found</h2>
            </template>
            <!-- Custom slot for edit button -->
            <template #[`item.actions`]="{ item, index }">
              <DataTableActions
                :actions="dataTableActions"
                :data="{ index, item }"
              />
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <!-- Total Fees -->
        <h3 class="text-h6 font-weight-bold mb-7">Total Fees</h3>
        <div class="d-flex justify-space-between align-start">
          <p class="text--secondary">Flat amount</p>
          <p class="font-weight-medium">{{ feeFlatAmountTotal | currency }}</p>
        </div>
        <div class="d-flex justify-space-between align-start">
          <p class="text--secondary">Variable amount</p>
          <p class="font-weight-medium">{{ feeVariableAmount | currency }}</p>
        </div>
        <!-- Total Roll Up -->
        <h3 class="text-h6 font-weight-bold mb-7 mt-5">Total Roll Up</h3>
        <div class="d-flex justify-space-between align-start">
          <p class="text--secondary">Flat amount</p>
          <p class="font-weight-medium">
            {{ rollupFlatAmountTotal | currency }}
          </p>
        </div>
        <div class="d-flex justify-space-between align-start">
          <p class="text--secondary">Variable amount</p>
          <p class="font-weight-medium">
            {{ rollupVariableAmountTotal | currency }}
          </p>
        </div>
        <!-- Loan Summary -->
        <h3 class="text-h6 font-weight-bold mb-7 mt-5">Loan Summary</h3>
        <div class="d-flex justify-space-between align-start">
          <p class="text--secondary">Loan amount with roll up</p>
          <p class="font-weight-medium">
            {{ totalLoanAmountWithRollup || 0 | currency }}
          </p>
        </div>
      </v-col>
    </v-row>
    <!-- Dialog used to edit -->
    <RollUpItem @itemSent="setUpdatedItem" ref="rollUpModal" />
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import RollUpItem from '@/modules/loanpro/components/loan-pro/WorkLoad/RollUpItem.vue';
import { cloneDeep } from 'lodash';
export default {
  mixins: [PageMixin],

  components: { RollUpItem },

  props: {
    gct: {
      type: Number,
      default: 15
    },
    loanAmount: {
      type: Number,
      default: 0
    },
    fees: {
      type: Array,
      default: () => []
    }
  },

  emits: ['loanFeesUpdated', 'feeDetailsChanged'],

  watch: {
    fees: {
      handler(val) {
        this.localFees = val;
      },
      deep: true
    },
    totalLoanAmountWithRollup() {
      this.emitValues();
    }
  },

  data() {
    return {
      localFees: [],
      loan: {},
      // Actions for edit button in datatable
      dataTableActions: [
        {
          icon: 'mdi-pencil-outline',
          action: 'Edit',
          actionCallBack: (index, item) => {
            this.$refs.rollUpModal.open(cloneDeep(item));
          }
        }
      ],
      /** @type {import('vuetify').DataTableHeader[]} */
      headers: [
        { text: 'Items', value: 'feeName', sortable: false },
        { text: 'Total Policy Rate', value: 'rate' },
        { text: 'Roll Up Percentage', value: 'percentage' },
        { text: 'Total Flat Fee', value: 'flatFee' },
        { text: 'Roll Up Amount', value: 'amount' },
        { text: 'Actions', align: 'center', value: 'actions', sortable: false }
      ]
    };
  },

  methods: {
    getFeeDetails() {
      const details = {
        feeFlatAmountTotal: +Number(this.feeFlatAmountTotal || 0).toFixed(2),
        feeVariableAmount: +Number(this.feeVariableAmount || 0).toFixed(2),
        rollupVariableAmountTotal: +Number(
          this.rollupVariableAmountTotal || 0
        ).toFixed(2),
        rollupFlatAmountTotal: +Number(this.rollupFlatAmountTotal || 0).toFixed(
          2
        ),
        totalLoanAmountWithRollup: +Number(
          this.totalLoanAmountWithRollup || 0
        ).toFixed(2)
      };

      const feeDetails = {
        fees: this.localFees,
        ...details
      };

      return feeDetails;
    },

    emitValues() {
      this.$emit('feeDetailsChanged', this.getFeeDetails());
    },

    // The `setUpdatedItem` method is used to update the `localFees` array with the updated item.
    setUpdatedItem(item) {
      this.localFees = this.localFees.map((fee) => {
        if (fee.feeName === item.feeName) {
          const percentageAmount =
            ((item?.rollUpPercentage || 0) / 100) * this.loanAmount || 0;
          const flatAmount = item?.rollUpAmount || 0;

          const totalRollUpAmount = percentageAmount + flatAmount;

          return { ...item, totalRollUpAmount };
        }
        return fee;
      });
    }
  },

  computed: {
    feeFlatAmountTotal() {
      let total = 0;
      this.localFees.forEach((fee) => {
        if (fee.attractGct) {
          total += Number(fee.flatFeeWithGct);
        } else {
          total += Number(fee.flatFee);
        }
      });
      return Number(total);
    },

    feeVariableAmount() {
      let total = 0;
      this.localFees.forEach((fee) => {
        if (fee.attractGct) {
          total += Number(fee.feeRatePercentageWithGct);
        } else {
          total += +fee.feeRate;
        }
      });
      const percentage = total / 100;
      const amount = Number(percentage) * this.loanAmount;
      return Number(amount.toFixed(2));
    },

    rollupVariableAmountTotal() {
      let totalPercentage = 0;
      this.localFees.forEach((fee) => {
        totalPercentage += Number(fee.rollUpPercentage / 100 || 0);
      });
      let totalLoanWithFlat = this.rollupFlatAmountTotal + this.loanAmount;
      return (
        Number(totalLoanWithFlat) / (1 - totalPercentage) - totalLoanWithFlat
      );
    },

    rollupFlatAmountTotal() {
      let total = 0;
      this.localFees.forEach((fee) => {
        total += Number(fee.rollUpAmount);
      });
      return Number(total);
    },

    totalLoanAmountWithRollup() {
      return (
        this.rollupVariableAmountTotal +
        this.rollupFlatAmountTotal +
        this.loanAmount
      );
    }
  }
};
</script>
